import React, { useState } from "react"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownFooter,
  DropdownBody,
  DropdownMenuHint,
  DropdownMenuDivider,
} from "~/react/ui/dropdown-menu"
import { Button, ButtonSuffix } from "~/react/ui/button"
import { api_leads_lists_path } from "~/lib/routes"
import { Select } from "~/react/ui/select"
import { Radio } from "~/react/ui/radio"
import { CurrencyHelper } from "~/helpers/currency_helper"
import { Icon } from "~/react/ui/icon"

export interface LeadSaveManagerListPickerProps {
  id: string
  error: any
  leadsLists: { id: number; name: string }[]
  setListToUse: (list: { name: string; id: number | null }) => void
  listToUse: { name: string; id: number | null } | null
}
export const LeadSaveManagerListPicker = ({
  id,
  error,
  leadsLists,
  setListToUse,
  listToUse,
}: LeadSaveManagerListPickerProps) => {
  const [listType, setListType] = useState<"existing" | "new">(leadsLists.length > 0 ? "existing" : "new")
  const [listName, setListName] = useState<string>("")
  const [listNameError, setListNameError] = useState<any>(error)
  const [selectedListId, setSelectedListId] = useState<number>(leadsLists[0]?.id)

  const handleListNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setListName(e.target.value)
    setListNameError(null)
    setListToUse({ name: e.target.value, id: null })
  }

  const selectList = ({ value }: { value: number }) => {
    const leadsList = leadsLists.find((list) => list.id === value) as {
      id: number
      name: string
    }

    setListName("")
    setListNameError(null)
    setListToUse({ name: leadsList.name, id: leadsList.id })
    setSelectedListId(leadsList.id)
  }

  return (
    <>
      <div className="h-form-group mb-4">
        <div className="h-form-group__input">
          <Radio
            id={`lead-${id}-save-manager-existing-lists`}
            name={`lead-${id}-save-manager-list-type`}
            checked={listType === "existing"}
            onChange={() => setListType("existing")}
          />
        </div>
        <div className="h-form-group__infos">
          <label className="h-label mb-2" htmlFor={`lead-${id}-save-manager-existing-lists`}>
            {i18n.t("js.multi_domain_search.lead_save_manager.list_picker.save_to_existing_list")}
          </label>

          {listType === "existing" && (
            <>
              <label className="sr-only" htmlFor={`lead-${id}-save-manager-lists`}>
                {i18n.t("js.multi_domain_search.lead_save_manager.list_picker.select_list")}
              </label>

              <Select
                options={leadsLists.map((list) => ({
                  value: list.id,
                  label: list.name,
                }))}
                placeholder={i18n.t("js.multi_domain_search.lead_save_manager.list_picker.select_list_placeholder")}
                isClearable={false}
                isMulti={false}
                onChange={selectList}
                defaultValue={selectedListId}
              />
            </>
          )}
        </div>
      </div>

      <div className="h-form-group">
        <div className="h-form-group__input">
          <Radio
            id={`lead-${id}-save-manager-new-list`}
            name={`lead-${id}-save-manager-list-type`}
            checked={listType === "new"}
            onChange={() => setListType("new")}
          />
        </div>
        <div className="h-form-group__infos">
          <label className="h-label mb-2" htmlFor={`lead-${id}-save-manager-new-list`}>
            {i18n.t("js.multi_domain_search.lead_save_manager.list_picker.create_new_list")}
          </label>

          {listType === "new" && (
            <>
              <label className="sr-only" htmlFor={`lead-${id}-save-manager-list-name`}>
                {i18n.t("js.multi_domain_search.lead_save_manager.list_picker.name_of_list")}
              </label>
              <input
                className="h-input h-input--sm h-input--block"
                type="text"
                id={`lead-${id}-save-manager-list-name`}
                placeholder={i18n.t("js.multi_domain_search.lead_save_manager.list_picker.name_of_list")}
                value={listName}
                onChange={handleListNameChange}
              />

              {listNameError && (
                <div className="h-hint h-hint--error mt-2">
                  <span className="far fa-circle-exclamation" aria-hidden="true"></span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}

export const createLeadList = async (name: string): Promise<{ name: string; id: number }> => {
  const response = await fetch(api_leads_lists_path({ api_version: "v2", api_key: gon.api_key }), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ name }),
  })

  if (!response.ok) {
    const responseData = await response.json()
    App.displayFlash("error", responseData.errors[0].details)
    return null
  }

  const json = await response.json()
  return json.data
}

export interface LeadSaveManagerProps {
  className?: string
  id: string
  leadsLists: any[]
  onSave: (name: string, listId: number) => void
  disableButtons: boolean
  children: React.ReactNode
  cost: number
  hint?: string
  customPosition?: (triggerElement: HTMLElement) => void
}
export const LeadSaveManager = ({
  className,
  id,
  leadsLists,
  onSave,
  children,
  disableButtons,
  cost,
  hint,
  customPosition,
}: LeadSaveManagerProps) => {
  const [listToUse, setListToUse] = useState<{
    name: string
    id: number
  } | null>(leadsLists[0])
  const [listNameError, setListNameError] = useState<any>(null)
  const [isSaving, setIsSaving] = useState<boolean>(false)

  const handleDismiss = (event: React.MouseEvent<HTMLButtonElement>) => {
    const dropdownElement = event.currentTarget.closest(".dropdown")
    if (dropdownElement) {
      const dropdownToggle = dropdownElement.querySelector('[data-bs-toggle="dropdown"]')

      if (dropdownToggle && (window as any).bootstrap) {
        const dropdown = (window as any).bootstrap.Dropdown.getInstance(dropdownToggle)

        if (dropdown) {
          dropdown.hide()
        }
      }
    }
  }

  const handleSave = async () => {
    setIsSaving(true)

    if (listToUse.id) {
      onSave(listToUse.name, listToUse.id)
    } else {
      const data = await createLeadList(listToUse.name)
      if (!data) {
        setListNameError("Failed to create list")
        setIsSaving(false)

        return
      }
      onSave(data.name, data.id)
    }

    setIsSaving(false)
  }

  return (
    <DropdownMenu className={className}>
      <DropdownMenuTrigger asChild customPosition={customPosition}>
        {children}
      </DropdownMenuTrigger>

      <DropdownMenuContent className="lead-save-manager overflow-visible">
        {hint && (
          <>
            <DropdownMenuHint className="m-2">
              <Icon icon="info-circle" size="xs" />
              {hint}
            </DropdownMenuHint>
            <DropdownMenuDivider className="mx-2" />
          </>
        )}
        <DropdownBody className={hint ? "pt-2" : undefined}>
          <LeadSaveManagerListPicker
            id={id}
            leadsLists={leadsLists}
            setListToUse={setListToUse}
            error={listNameError}
          />
        </DropdownBody>

        <DropdownFooter>
          <Button variant="primary" size="sm" onClick={handleSave} disabled={disableButtons || isSaving}>
            {isSaving || disableButtons
              ? i18n.t("js.multi_domain_search.lead_save_manager.revealing")
              : i18n.t("js.multi_domain_search.lead_save_manager.reveal")}
            {cost !== null && (
              <ButtonSuffix>
                <span className="cost-indicator cost-indicator--bare">
                  <Icon icon="coin" size="xs" className="cost-indicator__icon" />
                  {CurrencyHelper.numberWithDelimiter(cost)}
                </span>
              </ButtonSuffix>
            )}
          </Button>
          <Button variant="ghost" size="sm" onClick={handleDismiss} disabled={disableButtons}>
            {i18n.t("js.multi_domain_search.lead_save_manager.dismiss")}
          </Button>
        </DropdownFooter>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
